import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Section, SectionInner, Mark } from "../../globalStyles";
import {
  DarkBand,
  DarkHeading,
  DarkSub,
  ChartsGrid,
  ChartBlock,
  ChartTitle,
  ChartSvg,
  AxisLabel,
  ValueLabel,
  HistoryTitle,
  History,
  YearRow,
  YearLabel,
  EventList,
  EventItem,
  EventMonth,
  EventText,
} from "./GrowthSection.elements";

const Baseline = () => (
  <line
    x1={PLOT.left}
    x2={PLOT.right}
    y1={PLOT.bottom}
    y2={PLOT.bottom}
    style={{ stroke: "var(--chart-rule)" }}
    strokeWidth="1"
  />
);

const W = 300;
const PLOT = { left: 22, right: 282, top: 16, bottom: 120 };
const BASE_H = 150;

const px = (i, n) => PLOT.left + (i / (n - 1)) * (PLOT.right - PLOT.left);
const py = (v, max) =>
  PLOT.bottom - (v / max) * (PLOT.bottom - PLOT.top);

const smoothPath = (pts) => {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const cx = (p0.x + p1.x) / 2;
    d += ` C ${cx} ${p0.y} ${cx} ${p1.y} ${p1.x} ${p1.y}`;
  }
  return d;
};

const AreaChart = ({ data, labels }) => {
  const max = Math.max(...data) * 1.1;
  const pts = data.map((v, i) => ({ x: px(i, data.length), y: py(v, max) }));
  const line = smoothPath(pts);
  const area = `${line} L ${pts[pts.length - 1].x} ${PLOT.bottom} L ${pts[0].x} ${PLOT.bottom} Z`;
  return (
    <ChartSvg viewBox={`0 0 ${W} ${BASE_H}`}>
      <path d={area} style={{ fill: "var(--chart-fill)" }} fillOpacity="0.28" />
      <Baseline />
      <path
        d={line}
        fill="none"
        style={{ stroke: "var(--chart-line)" }}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {labels.map((lb, i) => (
        <AxisLabel key={i} x={px(i, data.length)} y={142} textAnchor="middle">
          {lb}
        </AxisLabel>
      ))}
    </ChartSvg>
  );
};

const BarChart = ({ data, labels }) => {
  const max = Math.max(...data) * 1.15;
  const n = data.length;
  const slot = (PLOT.right - PLOT.left) / n;
  const bw = slot * 0.5;
  return (
    <ChartSvg viewBox={`0 0 ${W} ${BASE_H}`}>
      <Baseline />
      {data.map((v, i) => {
        const cx = PLOT.left + slot * i + slot / 2;
        const y = py(v, max);
        const h = PLOT.bottom - y;
        return (
          <g key={i}>
            <rect
              x={cx - bw / 2}
              y={y}
              width={bw}
              height={h}
              rx="3"
              style={{ fill: "var(--chart-fill)" }}
            />
            <ValueLabel x={cx} y={y - 8} textAnchor="middle">
              {v}%
            </ValueLabel>
            <AxisLabel x={cx} y={142} textAnchor="middle">
              {labels[i]}
            </AxisLabel>
          </g>
        );
      })}
    </ChartSvg>
  );
};

const LineChart = ({ data, labels, suffix }) => {
  const max = Math.max(...data) * 1.25;
  const pts = data.map((v, i) => ({ x: px(i, data.length), y: py(v, max) }));
  const line = smoothPath(pts);
  return (
    <ChartSvg viewBox={`0 0 ${W} ${BASE_H}`}>
      <Baseline />
      <path
        d={line}
        fill="none"
        style={{ stroke: "var(--chart-ink)" }}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="5" style={{ fill: "var(--chart-fill)", stroke: "var(--chart-dot)" }} strokeWidth="2" />
          <ValueLabel x={p.x} y={p.y - 12} textAnchor="middle">
            {data[i]}
            {suffix}
          </ValueLabel>
          <AxisLabel x={p.x} y={142} textAnchor="middle">
            {labels[i]}
          </AxisLabel>
        </g>
      ))}
    </ChartSvg>
  );
};

const GrowthSection = () => {
  const [language, setLanguage] = useState("ko");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/en") setLanguage("en");
    else if (location.pathname === "/ko") setLanguage("ko");
    else setLanguage(localStorage.getItem("language") || "ko");

    const handleLanguageChange = (e) => setLanguage(e.detail);
    window.addEventListener("languageChange", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChange", handleLanguageChange);
  }, [location.pathname]);

  const content = {
    ko: {
      heading: ["바삭한소프트는 늘 ", "지금이 저점", "입니다"],
      sub: "여태 그랬던 것처럼, 앞으로도 성장합니다.",
      charts: {
        revenue: "안정적인 매출 성장",
        overseas: "해외 매출 비중",
        team: "팀원 수 성장",
      },
      revenueLabels: ["2023", "2024", "2025", "2026 (예상)"],
      overseasLabels: ["2023", "2024", "2025", "2026 (5월~)"],
      teamLabels: ["2023", "2024", "2025", "2026 (예정)"],
      teamSuffix: "명",
      historyTitle: "주요 연혁",
      history: [
        {
          year: "2022",
          events: [{ month: "10월", text: "‘<b>엽차엽차</b>’ 설립" }],
        },
        {
          year: "2023",
          events: [
            { month: "11월", text: "<b>수험생 키우기</b> 한국 런칭" },
            { month: "12월", text: "한국방송미디어공학회 추계학술대회 <b>논문 우수상</b>" },
          ],
        },
        {
          year: "2024",
          events: [
            { month: "3월", text: "중소벤처기업부 창업중심대학 지원기업 선정" },
            { month: "5월", text: "‘<b>바삭한소프트</b>’로 사명 변경 · 머니투데이 우수스타트업 대상" },
            { month: "10월", text: "‘수험생 키우기’ <b>일본 진출</b> · 일본 플레이스토어 피처링 선정" },
          ],
        },
        {
          year: "2025",
          events: [
            { month: "4월", text: "구글 <b>창구프로그램 7기</b> 선정" },
            { month: "5월", text: "한국콘텐츠진흥원 게임더하기 사업 선정" },
            { month: "7월", text: "모바일 시뮬레이션 게임 개발 시스템 <b>특허 등록</b>" },
            { month: "12월", text: "<b>대만 앱스토어 전체 게임 1위</b> 달성" },
          ],
        },
        {
          year: "2026",
          events: [
            { month: "4월", text: "2026 중기부 창업중심대학 사업 선정" },
            { month: "5월", text: "‘Korean Student Simulator’ 글로벌 런칭 · <b>글로벌 유저 150만 돌파</b>" },
            { month: "5월", text: "한국콘텐츠진흥원 글로벌 웹툰 게임스 수행기업 선정 (연애혁명 키우기)" },
          ],
        },
      ],
    },
    en: {
      heading: ["Basakansoft is ", "always at its floor", ""],
      sub: "Just as it always has, it keeps growing.",
      charts: {
        revenue: "Steady revenue growth",
        overseas: "Overseas revenue share",
        team: "Team growth",
      },
      revenueLabels: ["2023", "2024", "2025", "2026 (est.)"],
      overseasLabels: ["2023", "2024", "2025", "2026 (May~)"],
      teamLabels: ["2023", "2024", "2025", "2026 (est.)"],
      teamSuffix: "",
      historyTitle: "Milestones",
      history: [
        {
          year: "2022",
          events: [{ month: "Oct", text: "Founded as ‘<b>Yeopcha Yeopcha</b>’" }],
        },
        {
          year: "2023",
          events: [
            { month: "Nov", text: "<b>Student Maker</b> launched in Korea" },
            { month: "Dec", text: "<b>Best Paper Award</b>, KIBME Fall Conference" },
          ],
        },
        {
          year: "2024",
          events: [
            { month: "Mar", text: "Selected for the Startup-Centered University program (MSS)" },
            { month: "May", text: "Renamed to ‘<b>Basakansoft</b>’ · MoneyToday Best Startup Award" },
            { month: "Oct", text: "Student Maker <b>launched in Japan</b> · Play Store featured" },
          ],
        },
        {
          year: "2025",
          events: [
            { month: "Apr", text: "Selected for Google <b>Changgu Program (7th)</b>" },
            { month: "May", text: "Selected for KOCCA Game Plus program" },
            { month: "Jul", text: "<b>Patent registered</b> for mobile simulation game dev system" },
            { month: "Dec", text: "<b>#1 across all games on Taiwan App Store</b>" },
          ],
        },
        {
          year: "2026",
          events: [
            { month: "Apr", text: "Selected for the 2026 MSS Startup-Centered University program" },
            { month: "May", text: "‘Korean Student Simulator’ global launch · <b>1.5M global users</b>" },
            { month: "May", text: "Selected for KOCCA Global Webtoon Games (Love Revolution Maker)" },
          ],
        },
      ],
    },
  };

  const c = content[language];

  return (
    <>
      <DarkBand id="company">
        <SectionInner>
          <DarkHeading>
            {c.heading[0]}
            <Mark>{c.heading[1]}</Mark>
            {c.heading[2]}
          </DarkHeading>
          <DarkSub>{c.sub}</DarkSub>

          <ChartsGrid>
            <ChartBlock>
              <ChartTitle>{c.charts.revenue}</ChartTitle>
              <AreaChart data={[16, 40, 52, 100]} labels={c.revenueLabels} />
            </ChartBlock>
            <ChartBlock>
              <ChartTitle>{c.charts.overseas}</ChartTitle>
              <BarChart data={[14.2, 21.4, 31.5, 63.1]} labels={c.overseasLabels} />
            </ChartBlock>
            <ChartBlock>
              <ChartTitle>{c.charts.team}</ChartTitle>
              <LineChart
                data={[2, 7, 12, 20]}
                labels={c.teamLabels}
                suffix={c.teamSuffix}
              />
            </ChartBlock>
          </ChartsGrid>
        </SectionInner>
      </DarkBand>

      <Section>
        <SectionInner>
          <HistoryTitle>{c.historyTitle}</HistoryTitle>
          <History>
            {c.history.map((y) => (
              <YearRow key={y.year}>
                <YearLabel>{y.year}</YearLabel>
                <EventList>
                  {y.events.map((e, i) => (
                    <EventItem key={i}>
                      <EventMonth>{e.month}</EventMonth>
                      <EventText dangerouslySetInnerHTML={{ __html: e.text }} />
                    </EventItem>
                  ))}
                </EventList>
              </YearRow>
            ))}
          </History>
        </SectionInner>
      </Section>
    </>
  );
};

export default GrowthSection;
